"use client";

import clsx from "clsx";
import React, { useEffect, useState } from "react";
import posthog from "posthog-js";
import { toast } from "sonner";
import { Button } from "@/components/common";
import { BTN_TYPES } from "@/utils/constants";
import useCart from "@/cart/useCart";
import type { AddItemsLine } from "@/cart/addItems";
import type { CartAttribute } from "@/cart/cartAttributes";
import { getVariantQuantity } from "@/cart/displayCart";
import useLayoutStore from "@/stores/useLayoutStore";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";

const NO_ADD_ON_LINES: readonly AddItemsLine[] = [];

type AddToCartButtonProps = {
  children: React.ReactNode;
  btnType?: string;
  productId: string;
  quantity?: number;
  /**
   * Extra lines to add alongside the product, in one batched mutation. The
   * caller owns each line's variant and quantity — the button never derives
   * add-ons from the main product.
   */
  addOnLines?: readonly AddItemsLine[];
  /** Attributes on the product's own line. */
  attributes?: readonly CartAttribute[];
  /** Attributes set on the cart as the lines land. */
  cartAttributes?: Record<string, string>;
  className?: string;
  quantityAvailable?: number;
  availableForSale: boolean;
  gtmButtonName?: string;
  showQuantityAndCartIcon?: boolean;
  buttonLocation?: string;
  posthogEventName?: string;
  /**
   * Runs on click before the cart mutation, so an add that later fails or is
   * abandoned is still recorded. Callers that need typed, product-specific
   * properties use this instead of `posthogEventName`.
   */
  onBeforeAdd?: () => void;
  onSuccess?: () => void;
};

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  children,
  btnType = BTN_TYPES.PRIMARY,
  productId,
  quantity = 1,
  addOnLines = NO_ADD_ON_LINES,
  attributes,
  cartAttributes,
  className = "",
  availableForSale,
  gtmButtonName = "",
  showQuantityAndCartIcon = false,
  buttonLocation,
  posthogEventName,
  onBeforeAdd,
  onSuccess,
}) => {
  const { cart, addItem, addItems, addMatic } = useCart();
  const openSideCart = useLayoutStore((state) => state.openSideCart);
  const cartQuantity = cart?.totalQuantity || 0;
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [quantityAndCartIcon, setQuantityAndCartIcon] = useState(false);
  const [, setIsHovered] = useState(false);
  const cartItemQuantity = getVariantQuantity(cart, productId);

  const [currentQuantity, setCurrentQuantity] = useState(
    cartItemQuantity || quantity,
  );

  const goToCartCheckout = () => {
    if (cartQuantity === 0) return;
    openSideCart();
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
        setQuantityAndCartIcon(true);
      }, 2500);
    }

    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    if (cartItemQuantity > 0) {
      setCurrentQuantity(cartItemQuantity);
    } else {
      setCurrentQuantity(quantity);
    }
  }, [cartItemQuantity, quantity]);

  const handleAddToCart = async () => {
    // A second tap while the first is in flight does nothing; the button
    // stays enabled-looking meanwhile (disabling it swapped in the disabled
    // look mid-add).
    if (!availableForSale || isLoading) return;
    setIsLoading(true);
    onBeforeAdd?.();

    // Track button click event
    if (posthogEventName) {
      posthog.capture(posthogEventName, {
        button_location: buttonLocation,
        product_id: productId,
        button_text: typeof children === "string" ? children : "ADD TO CART",
        page_path:
          typeof window !== "undefined" ? window.location.pathname : "",
        is_available: availableForSale,
      });
    }

    if (gtmButtonName) {
      window.dataLayer?.push({
        event: "reserve_now",
        value: gtmButtonName,
      });
    }
    try {
      const addContext = {
        source: buttonLocation?.includes("pdp") ? "pdp" : "other",
        buttonLocation: buttonLocation,
      };

      // The caller's add-on lines ride along in the same mutation as the
      // product, so the whole configuration lands in one round-trip.
      if (
        addOnLines.length > 0 ||
        attributes !== undefined ||
        cartAttributes !== undefined
      ) {
        const lines = [
          attributes === undefined
            ? { variantId: productId, quantity }
            : { variantId: productId, quantity, attributes },
          ...addOnLines,
        ];
        if (cartAttributes === undefined) {
          await addItems(lines, addContext);
        } else {
          await addItems(lines, addContext, { cartAttributes });
        }
      } else if (isMaticFamilyVariantId(productId)) {
        await addMatic(addContext, quantity, { variantId: productId });
      } else {
        await addItem(productId, addContext, quantity);
      }

      setShowSuccess(true);
      onSuccess?.();
      openSideCart("auto_after_add");
    } catch (error) {
      console.error("Error adding to cart:", error);
      const message =
        "The item could not be added to your cart. Please try again.";
      toast.error(message, { toasterId: "global" });
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div
        /* Geometry stays the caller's: this state used to bring its own padding,
           font size and `btn-secondary`, which resized the button the moment it
           succeeded. */
        className={clsx(
          className,
          "relative z-1 cursor-auto border bg-legacy-white text-legacy-primary",
        )}
      >
        {/* The idle label still lays the box out, so the button cannot resize;
            the success content is lifted out of flow on top of it. */}
        <span aria-hidden className="invisible flex items-center">
          {children}
        </span>
        <span className="absolute inset-0 flex items-center justify-center gap-xs whitespace-nowrap font-medium uppercase">
          <svg
            width="19"
            height="14"
            viewBox="0 0 21 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1 8.28L7.16216 15L20 1" stroke="#1F2022" />
          </svg>
          Added!
        </span>
      </div>
    );
  }

  if (cartItemQuantity > 0 && quantityAndCartIcon && showQuantityAndCartIcon) {
    return (
      <button
        className="btn-secondary relative z-1 flex h-[60px] cursor-pointer items-center gap-[12px] bg-legacy-white px-[32px] text-center text-[18px] font-medium hover:bg-legacy-white hover:text-legacy-primary md:gap-[23px]"
        onClick={goToCartCheckout}
      >
        {currentQuantity}
        <svg
          width="24"
          height="20"
          viewBox="0 0 24 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.3515 19.5H6.69091L5.8351 14.1977M5.8351 14.1977H21.0485L23 3.59302H4.12347M5.8351 14.1977L4.12347 3.59302M4.12347 3.59302L3.62424 0.5H0"
            stroke="#1F2022"
          />
        </svg>
      </button>
    );
  }

  const mouseEnter = () => {
    setIsHovered(true);
  };

  const mouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <Button
      onClickFn={handleAddToCart}
      btnType={btnType}
      disabled={!availableForSale}
      busy={isLoading}
      customClasses={className}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
    >
      {availableForSale ? (
        showQuantityAndCartIcon && !isLoading ? (
          <span className="group flex items-center gap-[16px]">
            <span className="hidden text-lg font-medium uppercase text-legacy-white md:block md:group-hover:hidden">
              <svg
                width="19"
                height="19"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.81373 19.5V11H0.5V8.39463H8.81373V0.5H11.5V8.39463H19.5V11H11.5V19.5H8.81373Z"
                  fill="white"
                />
              </svg>
            </span>
            <span className="px-[1px] md:hidden md:px-[2px] md:group-hover:block">
              {children}
            </span>
          </span>
        ) : (
          // While the add is in flight the label dims and softens, so the
          // tap reads as heard until the cart answers.
          <span
            className={clsx(
              "inline-flex items-center transition-[opacity,filter] duration-quick ease-out-strong motion-reduce:transition-none",
              isLoading && "opacity-60 blur-[1px] motion-reduce:blur-none",
            )}
          >
            {children}
          </span>
        )
      ) : (
        <span className="font-medium uppercase text-legacy-disable">
          Out of stock
        </span>
      )}
    </Button>
  );
};

export default AddToCartButton;
